import { useEffect, useState } from "react";

import { Copy, Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const mcpUrl = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/mcp`;

export default function Connect() {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = "Connect an AI assistant to Dialgia | Agent Integrations";
    return () => {
      document.title = prev;
    };
  }, []);

  const copy = async () => {
    await navigator.clipboard.writeText(mcpUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };


  return (
    <>
      <main className="min-h-screen bg-background pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <header className="mb-12">

            <p className="text-sm font-medium text-muted-foreground tracking-wide uppercase mb-3">
              Agent integrations
            </p>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
              Connect an AI assistant to Dialgia
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Add Dialgia to ChatGPT or Claude so your assistant can act on your
              account — signed in as you.
            </p>
          </header>

          <Card className="p-6 md:p-8 mb-12 border-border/60">
            <label className="text-sm font-medium text-muted-foreground block mb-2">
              MCP server URL
            </label>
            <div className="flex items-center gap-3">
              <code className="flex-1 px-4 py-3 bg-muted rounded-lg text-sm md:text-base font-mono break-all">
                {mcpUrl}
              </code>
              <Button onClick={copy} size="lg" variant="secondary" className="shrink-0">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              You'll paste this into your AI assistant below. Signing in uses your
              Dialgia account.
            </p>
          </Card>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold tracking-tight mb-6">ChatGPT</h2>
            <ol className="space-y-4 text-base leading-relaxed">
              <li className="flex gap-4">
                <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">1</span>
                <div>
                  Open{" "}
                  <a
                    href="https://chatgpt.com/#settings/Connectors/Advanced"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-11 items-center gap-1 px-1 text-primary underline underline-offset-4"
                  >
                    ChatGPT Connector settings <ExternalLink className="w-3.5 h-3.5" />
                  </a>{" "}
                  and enable Developer mode (read the risk notice shown there).
                </div>
              </li>
              <li className="flex gap-4">
                <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">2</span>
                <div>In the chat composer's <strong>+</strong> menu, turn on Developer mode.</div>
              </li>
              <li className="flex gap-4">
                <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">3</span>
                <div>Click <strong>Add sources</strong>, then <strong>Connect more</strong>.</div>
              </li>
              <li className="flex gap-4">
                <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">4</span>
                <div>Name the connector "Dialgia" and paste the MCP URL from above. Sign in when prompted.</div>
              </li>
              <li className="flex gap-4">
                <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">5</span>
                <div>Ask ChatGPT to use Dialgia — for example, "list my open tickets".</div>
              </li>
            </ol>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold tracking-tight mb-6">Claude</h2>
            <ol className="space-y-4 text-base leading-relaxed">
              <li className="flex gap-4">
                <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">1</span>
                <div>
                  Open{" "}
                  <a
                    href="https://claude.ai/customize/connectors?modal=add-custom-connector"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-11 items-center gap-1 px-1 text-primary underline underline-offset-4"
                  >
                    Claude custom connectors <ExternalLink className="w-3.5 h-3.5" />
                  </a>.
                </div>
              </li>
              <li className="flex gap-4">
                <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">2</span>
                <div>Name the connector "Dialgia" and paste the MCP URL from above. Sign in when prompted.</div>
              </li>
              <li className="flex gap-4">
                <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">3</span>
                <div>Enable the connector from the chat composer, then ask Claude to use Dialgia.</div>
              </li>
            </ol>
          </section>

          <Card className="p-6 border-border/60 bg-muted/30">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your assistant will act as you — with the same access you have in Dialgia.
              You can disconnect at any time from your AI assistant's connector settings.
            </p>
          </Card>
        </div>
      </main>
    </>
  );
}
